import z from 'zod';
import { Nested } from './nested';

const StyleCombiner = z.enum(['', '&', '|']);

const numberOrString = z.number().or(z.string());
// Looks a lot like React.CSSProperties
const inlineStyleSchema = z
  .object({
    alignSelf: z.string(),
    gridColumnEnd: numberOrString,
    gridColumnStart: numberOrString,
    gridRowEnd: numberOrString,
    gridRowStart: numberOrString,
    justifySelf: z.string(),
    order: z.number(),
  })
  .partial()
  .passthrough();

const sliderDataSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('arrow'),
    dir: z.enum(['left', 'right']),
  }),
  z.object({ type: z.literal('nav') }),
  z.object({ type: z.literal('slide') }),
  // "Slider" is the deprecated name for "slide"
  z.object({ type: z.literal('Slider') }),
  z.object({ type: z.literal('slides') }),
  z
    .object({
      type: z.literal('wrapper'),
      navSpacing: z.number().optional().default(0),
      navInvert: z.boolean().optional().default(false),
      /* More properties observed:
          navShadow: false,
          autoplay: false,
          delay: 4000,
          iconArrows: true,
          animation: 'slide',
          navNumbers: false,
          easing: 'ease',
          navRound: true,
          hideArrows: false,
          disableSwipe: false,
          duration: 500,
          infinite: true,
          autoMax: 0,
       */
    })
    .passthrough(),
]);

const tabsDataSchema = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal('wrapper'),
      current: z.string().optional(),
    })
    .passthrough(),
  z.object({ type: z.literal('content') }).passthrough(),
  z.object({ type: z.literal('link') }).passthrough(),
  z.object({ type: z.literal('pane') }).passthrough(),
  z.object({ type: z.literal('menu') }).passthrough(),
]);

const navbarDataSchema = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal('wrapper'),
      collapse: z.string(), // a breakpoint name
    })
    .passthrough(),
  z.object({ type: z.literal('button') }).passthrough(),
  z.object({ type: z.literal('code') }).passthrough(),
  z.object({ type: z.literal('link') }).passthrough(),
  z.object({ type: z.literal('menu') }).passthrough(),
  z.object({ type: z.literal('brand') }).passthrough(),
]);

const inlineStyleDataSchema = z
  .object({
    base: z
      .record(
        // breakpoint
        z.string(),
        z.object({ noPseudo: inlineStyleSchema }).passthrough(),
      )
      .optional(),
  })
  .passthrough();
export type InlineStyleData = z.infer<typeof inlineStyleDataSchema>;

const formDataSchema = z.union([
  z
    .object({
      type: z.literal('select').optional(),
      opts: z.array(z.object({ v: z.string(), t: z.string() })).default([]),
    })
    .passthrough(),
  z.object({ type: z.string(), opts: z.never().optional() }).passthrough(),
]);

const wfTagNodeSchema = z
  .object({
    _id: z.string(),
    text: z.never().optional(),
    classes: z.array(z.string()).default([]),
    // type if omitted for the "body" node
    type: z.string().optional(),
    tag: z.string(),
    data: z
      .object({
        attr: z.record(z.string(), z.any()).optional(),
        xattr: z
          .array(
            z.object({ name: z.string(), value: z.string() }).passthrough(),
          )
          .nullish(),
        button: z.boolean().optional(),
        // Replace unknown once this is better understood:
        //   I've only seen grid: { type: "container" }
        grid: z.union([z.string(), z.unknown()]).optional(),
        // I've only seen "inline" or ""
        block: z.string().optional(),
        style: inlineStyleDataSchema.optional(),
        slider: sliderDataSchema.optional(),
        tabs: tabsDataSchema.optional(),
        navbar: navbarDataSchema.optional(),
        form: formDataSchema.optional(),
        // Known to be "default" or "custom" for checkboxes and radio buttons
        inputType: z.string().optional(),
        img: z.object({ id: z.string().optional() }).optional(),
      })
      .passthrough()
      // it is very rare to have no `data` property.  It means nothing special,
      // just an old way of doing things.
      .default({}),
    v: z.string().optional(),
  })
  .passthrough();
const wfTextNodeSchema = z.object({
  _id: z.string(),
  text: z.literal(true),
  v: z.string(),
});
const wfNodeBaseSchema = z.union([wfTagNodeSchema, wfTextNodeSchema]);
// We can't Omit<..., children>, because that's break the discriminated union.
export type NestedNode = Nested<z.infer<typeof wfNodeBaseSchema>>;
export type NestedTagNode = Exclude<NestedNode, { text: true }>;
const wfNodeSchema = wfNodeBaseSchema.and(
  z.object({
    children: z
      .array(z.string())
      .optional()
      .transform((x) => x ?? []),
  }),
);
export type WfFlatNode = z.infer<typeof wfNodeSchema>;

export const wfAssetSchema = z
  .object({ _id: z.string(), siteId: z.string(), fileName: z.string() })
  .passthrough();
export type WfAsset = z.infer<typeof wfAssetSchema>;

/*
const ix2InteractionSchema = z
  .object({ id: z.string(), target: z.string() })
  .passthrough();
const ix2TargetSchema = z.union([
  z.object({ appliesTo: z.literal('ELEMENT'), id: z.string() }).passthrough(),
  z
    .object({ appliesTo: z.literal('CLASS'), originalId: z.string() })
    .passthrough(),
  z
    .object({
      appliesTo: z.literal('ELEMENT_CLASS'),
      nodeId: z.string(),
      styleBlockIds: z.array(z.string()).default([]),
    })
    .passthrough(),
  z.object({}).passthrough(),
]);
const ix2EventSchema = z
  .object({
    id: z.string(),
    target: ix2TargetSchema,
    targets: z.array(ix2TargetSchema),
  })
  .passthrough();
*/
const ix2InteractionSchema = z.object({ id: z.string() }).passthrough();
const ix2EventSchema = z.object({ id: z.string() }).passthrough();
const ix2ActionItemSchema = z
  .object({
    id: z.string(),
    actionTypeId: z.string(),
    config: z
      .object({
        target: z
          .object({ nodeId: z.string().optional() })
          .passthrough()
          .optional(),
        value: z.string().or(z.number()).optional(),
        unit: z.string().optional(),
      })
      .passthrough(),
  })
  .passthrough();
const ix2ActionListSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    // The absence of actionItemGroups means something.  Do not add "default([])".
    //   Possibly when "continousParameterGroups" is present instead.
    // See: https://relume-io.slack.com/archives/C0402NL9EUS/p1692146570807199
    actionItemGroups: z
      .array(
        z
          .object({
            actionItems: z.array(ix2ActionItemSchema),
          })
          .passthrough(),
      )
      .optional(),
  })
  .passthrough();

const ix2Schema = z
  .object({
    interactions: z.array(ix2InteractionSchema).default([]),
    events: z.array(ix2EventSchema).default([]),
    actionLists: z.array(ix2ActionListSchema).default([]),
  })
  .passthrough();
export type IX2 = z.infer<typeof ix2Schema>;

const baseVariableSchema = z.object({
  id: z.string(),
  type: z.string(),
  name: z.string(),
  deleted: z.boolean(),
  createdOn: z.string(), // Assuming ISO date-time string
  updatedOn: z.string(),
});
const variableReferenceSchema = z.object({
  type: z.literal('ref'),
  value: z.object({ variableId: z.string() }),
});
const colorValueSchema = z.object({
  type: z.literal('color'),
  value: z.string(),
});
const colorVariableSchema = baseVariableSchema.extend({
  type: z.literal('color'),
  value: z.union([colorValueSchema, variableReferenceSchema]),
});
const lengthValueSchema = z.object({
  type: z.literal('length'),
  value: z.object({
    unit: z.string(),
    value: z.number(),
  }),
});
const lengthVariableSchema = baseVariableSchema.extend({
  type: z.literal('length'),
  value: z.union([lengthValueSchema, variableReferenceSchema]),
});
const fontFamilyValueSchema = z.object({
  type: z.literal('font-family'),
  value: z.string(),
});
const fontFamilyVariableSchema = baseVariableSchema.extend({
  type: z.literal('font-family'),
  value: z.union([fontFamilyValueSchema, variableReferenceSchema]),
});
export const wfVariableSchema = z.union([
  colorVariableSchema,
  lengthVariableSchema,
  fontFamilyVariableSchema,
]);
export const componentStyleSchema = z
  .object({
    _id: z.string(),
    fake: z.literal(false),
    type: z.enum(['class', 'tag']),
    name: z.string(),
    namespace: z.literal(''),
    comb: StyleCombiner,
    styleLess: z.string(),
    // [styleLessWithVariables] is created by us.
    // This allows us to do variable sync in the Chrome extension
    styleLessWithVariables: z
      .array(
        z.object({
          cssProperty: z.string(),
          name: z.string().optional(),
          value: z.string(),
        }),
      )
      .optional(),
    variants: z.record(z.string(), z.object({ styleLess: z.string() })),
    children: z.array(z.string()),
  })
  .passthrough();
const componentPayloadSchema = z
  .object({
    nodes: z.array(wfNodeSchema),
    styles: z.array(componentStyleSchema),
    assets: z.array(wfAssetSchema).default([]),
    ix1: z.any().optional(),
    ix2: ix2Schema.default({}),
  })
  .passthrough();
export const componentSchema = z
  .object({
    type: z.literal('@webflow/XscpData'),
    payload: componentPayloadSchema,
    meta: z
      .object({
        unlinkedSymbolCount: z.number().optional(),
        droppedLinks: z.number().optional(),
        dynBindRemovedCount: z.number().optional(),
        dynListBindRemovedCount: z.number().optional(),
        paginationRemovedCount: z.number().optional(),
      })
      .optional(),
  })
  .passthrough();

export type WfVariable = z.infer<typeof wfVariableSchema>;
export type Component = z.infer<typeof componentSchema>;
export type ComponentPayload = z.infer<typeof componentPayloadSchema>;
export type ComponentStyle = z.infer<typeof componentStyleSchema>;

const pageSchema = z
  .object({
    _id: z.string(),
    title: z.string().nullish(),
    slug: z.string().nullish(),
    utilKey: z.string().nullish(), // e.g. "search" or "401" or "404"
    type: z.enum(['page', 'folder']),
    parent: z.string().nullish(),
  })
  .passthrough();
const siteStyleSchema = z
  .object({
    _id: z.string(),
    data: z
      .object({
        type: z.enum(['class', 'tag']),
        name: z.string(),
        sel: z.string(),
        comb: StyleCombiner,
        styleLess: z.string(),
        children: z.array(z.string()),
        variants: z.record(
          z.string(),
          z.object({ sel: z.string(), styleLess: z.string() }),
        ),
        // pageId -> count of styles used on that page
        affects: z.record(z.string(), z.number()),
      })
      .passthrough(),
  })
  .passthrough();
const siteStyleMetadata = z
  .object({
    // I'm not sure these are always present, so I've added a nullish() transform:
    swatches: z
      .array(z.object({ id: z.string(), name: z.string(), color: z.string() }))
      .nullish()
      .transform((x) => x ?? []),
    breakpoints: z
      .record(
        z.string(),
        z.object({
          maxWidth: z.number().optional(),
          minWidth: z.number().optional(),
        }),
      )
      .nullish()
      .transform((x) => x ?? {}),
  })
  .passthrough();
export const siteStylesSchema = z
  .object({
    blocks: z.array(siteStyleSchema),
    data: siteStyleMetadata,
  })
  .passthrough();
export const siteSchema = z
  .object({
    siteId: z.string(),
    /**
     * This corresponds to the "clientAppVersion" in the webflow API.
     * Not the version of the site itself.
     */
    version: z.number(),
    pages: z.array(pageSchema),
    styles: siteStylesSchema,
    stylesVersion: z.number(),
    // pageId: z.string(),
    // list of dom nodes _in the current pageId only_!!
    domNodes: z.array(wfNodeSchema),
    symbols: z
      .array(wfNodeSchema)
      .optional()
      .transform((v) => v ?? []),
    variables: z.array(wfVariableSchema).optional(),
  })
  .passthrough();

export type SiteStyle = z.infer<typeof siteStyleSchema>;
export type SiteStyleMetadata = z.infer<typeof siteStyleMetadata>;
export type SiteStyles = z.infer<typeof siteStylesSchema>;
export type Page = z.infer<typeof pageSchema>;
export type Site = z.infer<typeof siteSchema>;
